import { DEFAULT_LOOK, WEAPONS, type PlayerProfile, type WeaponId } from '@tanky/shared';
import { api } from '../api';
import { h } from '../dom';
import { go, session, type Screen } from '../session';
import { tankThumb } from '../game/tankArt';
import { pct } from './leaderboard';

function tile(label: string, value: string, hint?: string) {
  return h('div', { class: 'tile', title: hint }, h('b', {}, value), h('span', {}, label));
}

function gamesText(n: number) {
  if (n === 1) return '1 dohraná hra';
  if (n >= 2 && n <= 4) return `${n} dohrané hry`;
  return `${n} dohraných her`;
}

function dateText(ms: number) {
  return new Date(ms).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
}

export function profileScreen(userId: string): Screen {
  const body = h('div', { class: 'page' }, h('p', { class: 'muted' }, 'Načítám…'));
  let destroyed = false;

  function render(p: PlayerProfile) {
    const t = p.totals;
    const kd = t.deaths ? (t.kills / t.deaths).toFixed(2) : t.kills ? `${t.kills}.00` : '–';
    const favorite = (Object.entries(p.weapons) as [WeaponId, number][]).sort((a, b) => b[1] - a[1]);
    const totalShots = favorite.reduce((s, [, n]) => s + n, 0);
    const record = (label: string, r: { value: number; gameId: string } | null, unit: string) =>
      h(
        'li',
        {},
        h('span', {}, label),
        r ? h('a', { href: `#/game/${r.gameId}`, title: 'Zobrazit hru' }, `${r.value} ${unit}`) : h('span', { class: 'muted' }, '–'),
      );
    const isMe = p.user.id === session.me?.id;

    body.replaceChildren(
      h('div', { class: 'topbar' }, h('button', { class: 'ghost', onclick: () => history.length > 1 ? history.back() : go('/') }, '← Zpět'), h('h2', {}, 'Profil')),
      h(
        'div',
        { class: 'card profile-head' },
        tankThumb(p.user.look ?? DEFAULT_LOOK, (p.user.look ?? DEFAULT_LOOK).color, 96, p.user.id),
        h('div', {}, h('h2', {}, p.user.name, isMe ? h('span', { class: 'badge', style: { marginLeft: '8px' } }, 'ty') : null), h('div', { class: 'muted small' }, t.games ? gamesText(t.games) : 'Zatím žádná dohraná hra')),
      ),
      h(
        'div',
        { class: 'tiles' },
        tile('Výhry', String(t.wins)),
        tile('% výher', pct(t.wins, t.games)),
        tile('Zabití', String(t.kills)),
        tile('K/D', kd, 'Zabití / vlastní zničení'),
        tile('Škody', String(t.damage), 'Způsobené škody soupeřům'),
        tile('Přesnost', pct(t.hits, t.shots), `${t.hits} zásahů z ${t.shots} výstřelů`),
        tile('Prům. umístění', t.games ? t.avgPlacement.toFixed(1) : '–'),
        tile('Sebezničení', String(t.suicides)),
      ),
      h(
        'div',
        { class: 'grid2' },
        h(
          'div',
          { class: 'card' },
          h('h3', {}, '🏅 Rekordy'),
          h(
            'ul',
            { class: 'records' },
            record('Nejsilnější zásah', p.records.bestHit, 'škod'),
            record('Nejdelší zásah', p.records.longestHit, 'm'),
            record('Nejvíc zabití v jedné hře', p.records.mostKills, '×'),
          ),
        ),
        h(
          'div',
          { class: 'card' },
          h('h3', {}, '🔫 Zbraně'),
          favorite.length
            ? h(
                'ul',
                { class: 'records' },
                ...favorite.map(([w, n], i) =>
                  h(
                    'li',
                    {},
                    h('span', {}, `${WEAPONS[w]?.icon ?? '•'} ${WEAPONS[w]?.name ?? w}${i === 0 ? ' ⭐' : ''}`),
                    h('span', { class: 'muted' }, `${n}× · ${pct(n, totalShots)}`),
                  ),
                ),
              )
            : h('p', { class: 'muted', style: { margin: '0' } }, 'Zatím nevystřelil.'),
        ),
      ),
      h(
        'div',
        { class: 'card' },
        h('h3', {}, '🕑 Poslední hry'),
        p.recent.length
          ? h(
              'div',
              { class: 'game-list' },
              ...p.recent.map((g) =>
                h(
                  'button',
                  { class: `game-item${g.won ? ' my-turn' : ''}`, onclick: () => go(`/game/${g.gameId}`) },
                  h('span', { class: 'names' }, `vs. ${g.opponents.join(', ') || '–'}`),
                  h('span', { class: `badge${g.won ? ' good' : ''}` }, g.won ? '🏆 Výhra' : g.placement ? `${g.placement}. z ${g.playerCount}` : '–'),
                  h('span', { class: 'muted small' }, `${g.damage} škod · ${g.kills}× zabití`),
                  h('span', { class: 'muted small' }, dateText(g.finishedAt)),
                ),
              ),
            )
          : h('p', { class: 'muted', style: { margin: '0' } }, 'Zatím žádné dohrané hry.'),
      ),
    );
  }

  api
    .playerStats(userId)
    .then((p) => !destroyed && render(p))
    .catch((e) => !destroyed && body.replaceChildren(h('p', { class: 'error' }, (e as Error).message), h('button', { onclick: () => go('/') }, '← Lobby')));

  return {
    el: h('div', { class: 'backdrop' }, body),
    destroy() {
      destroyed = true;
    },
  };
}
